import { useEffect, useState } from "react";
import AdminLayout from "@/pages/admin/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Upload, Link2, Loader2, RefreshCw, Trash2, Search, Package,
  ExternalLink, X, FolderOpen,
} from "lucide-react";

const CATEGORIES = [
  { v: "logo", l: "Logos" },
  { v: "label", l: "Label Artwork" },
  { v: "brand_guidelines", l: "Brand Guidelines" },
  { v: "color_palette", l: "Color Palette" },
  { v: "fonts", l: "Fonts" },
  { v: "website", l: "Website" },
  { v: "domain", l: "Domain" },
  { v: "packaging", l: "Packaging" },
  { v: "photography", l: "Photography" },
  { v: "other", l: "Other" },
];

const AdminBrandVault = () => {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [search, setSearch] = useState("");
  const [showUpload, setShowUpload] = useState(false);
  const [showLink, setShowLink] = useState(false);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filter) params.set("client_email", filter.trim().toLowerCase());
    api.get(`/admin/portal/vault?${params.toString()}`)
      .then((r) => setAssets(r.data.assets || []))
      .catch(() => setAssets([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [filter]);

  const filtered = assets.filter((a) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      (a.title || "").toLowerCase().includes(s) ||
      (a.client_email || "").toLowerCase().includes(s) ||
      (a.brand_name || "").toLowerCase().includes(s)
    );
  });

  const remove = async (id) => {
    if (!confirm("Remove this asset from the client's vault?")) return;
    try {
      await api.delete(`/admin/portal/vault/${id}`);
      toast.success("Asset removed.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to remove");
    }
  };

  return (
    <AdminLayout>
      <div className="mb-6 flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="ft-eyebrow mb-2">Client Success</div>
          <h1 className="text-3xl font-bold tracking-tight" data-testid="admin-vault-title">
            Brand Vault
          </h1>
          <p className="text-[var(--ft-muted)] mt-2 max-w-2xl">
            Manage every client&apos;s digital brand kit. Upload files (logos, labels, brand
            guidelines, fonts) or attach external links (website, domain, drive folders).
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="ft-btn-ghost" data-testid="admin-vault-refresh"><RefreshCw size={14} /> Refresh</button>
          <button onClick={() => setShowLink(true)} className="ft-btn-secondary" data-testid="admin-vault-add-link">
            <Link2 size={14} /> Add Link
          </button>
          <button onClick={() => setShowUpload(true)} className="ft-btn-primary" data-testid="admin-vault-upload-btn">
            <Upload size={14} /> Upload File
          </button>
        </div>
      </div>

      <div className="ft-card mb-4">
        <div className="grid md:grid-cols-2 gap-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ft-muted)]" />
            <input
              className="ft-input pl-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search title, brand, client…"
              data-testid="admin-vault-search"
            />
          </div>
          <input
            className="ft-input"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter by client email…"
            data-testid="admin-vault-client-filter"
          />
        </div>
      </div>

      {loading ? (
        <div className="ft-card fx-skeleton h-64" />
      ) : filtered.length === 0 ? (
        <div className="ft-card text-center py-16" data-testid="admin-vault-empty">
          <Package size={40} className="mx-auto mb-4 text-[var(--ft-muted)]" />
          <h3 className="text-lg font-semibold">No assets in the Vault yet.</h3>
        </div>
      ) : (
        <div className="grid gap-2">
          {filtered.map((a) => (
            <div key={a.id} className="ft-card flex items-start justify-between gap-4 flex-wrap" data-testid={`admin-vault-asset-${a.id}`}>
              <div className="min-w-0 flex-1">
                <div className="ft-eyebrow">{a.category?.replace("_", " ")} · {a.asset_type}</div>
                <div className="font-bold tracking-tight mt-1">{a.title}</div>
                <div className="text-xs text-[var(--ft-muted)] mt-1">
                  {a.brand_name ? `${a.brand_name} · ` : ""}{a.client_email} · Added {new Date(a.created_at).toLocaleDateString()}
                </div>
                {a.description && <div className="text-xs text-[var(--ft-charcoal)] mt-1">{a.description}</div>}
                {a.external_url && (
                  <a href={a.external_url} target="_blank" rel="noreferrer" className="text-xs text-[var(--ft-gold)] inline-flex items-center gap-1 mt-1">
                    <ExternalLink size={11} /> {a.external_url}
                  </a>
                )}
              </div>
              <button onClick={() => remove(a.id)} className="ft-btn-ghost" data-testid={`admin-vault-delete-${a.id}`}>
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {showUpload && <UploadModal onClose={() => setShowUpload(false)} onDone={() => { setShowUpload(false); load(); }} />}
      {showLink && <LinkModal onClose={() => setShowLink(false)} onDone={() => { setShowLink(false); load(); }} />}
    </AdminLayout>
  );
};

const UploadModal = ({ onClose, onDone }) => {
  const [form, setForm] = useState({ client_email: "", brand_name: "", brand_slug: "", category: "logo", title: "", description: "" });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const submit = async () => {
    if (!form.client_email) return toast.error("Client email required");
    if (!file) return toast.error("Choose a file");
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const params = new URLSearchParams();
      params.set("client_email", form.client_email.toLowerCase());
      if (form.brand_name) params.set("brand_name", form.brand_name);
      if (form.brand_slug) params.set("brand_slug", form.brand_slug);
      params.set("category", form.category);
      if (form.title) params.set("title", form.title);
      if (form.description) params.set("description", form.description);
      await api.post(`/admin/portal/vault/upload?${params.toString()}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Asset added to Brand Vault.");
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  return (
    <Modal title="Upload Vault Asset" onClose={onClose} testid="admin-vault-upload-modal">
      <Field label="Client Email *"><input className="ft-input" value={form.client_email} onChange={(e) => setForm((f) => ({ ...f, client_email: e.target.value }))} data-testid="admin-vault-upload-email" /></Field>
      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Brand Name"><input className="ft-input" value={form.brand_name} onChange={(e) => setForm((f) => ({ ...f, brand_name: e.target.value }))} /></Field>
        <Field label="Brand Slug"><input className="ft-input" value={form.brand_slug} onChange={(e) => setForm((f) => ({ ...f, brand_slug: e.target.value }))} /></Field>
      </div>
      <Field label="Category">
        <select className="ft-input" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} data-testid="admin-vault-upload-category">
          {CATEGORIES.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
        </select>
      </Field>
      <Field label="Title"><input className="ft-input" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} /></Field>
      <Field label="Description"><textarea className="ft-input min-h-[70px]" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} /></Field>
      <Field label="File *"><input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="admin-vault-upload-file" /></Field>
      <Footer onCancel={onClose} onSubmit={submit} loading={uploading} submitLabel="Upload" testid="admin-vault-upload-submit" />
    </Modal>
  );
};

const LinkModal = ({ onClose, onDone }) => {
  const [form, setForm] = useState({ client_email: "", brand_name: "", brand_slug: "", category: "website", title: "", description: "", external_url: "" });
  const [posting, setPosting] = useState(false);

  const submit = async () => {
    if (!form.client_email || !form.title || !form.external_url) return toast.error("Client email, title, and URL are required");
    setPosting(true);
    try {
      await api.post("/admin/portal/vault/link", { ...form, client_email: form.client_email.toLowerCase() });
      toast.success("Link added to Brand Vault.");
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add link");
    } finally { setPosting(false); }
  };

  return (
    <Modal title="Add External Link" onClose={onClose} testid="admin-vault-link-modal">
      <Field label="Client Email *"><input className="ft-input" value={form.client_email} onChange={(e) => setForm((f) => ({ ...f, client_email: e.target.value }))} data-testid="admin-vault-link-email" /></Field>
      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Brand Name"><input className="ft-input" value={form.brand_name} onChange={(e) => setForm((f) => ({ ...f, brand_name: e.target.value }))} /></Field>
        <Field label="Brand Slug"><input className="ft-input" value={form.brand_slug} onChange={(e) => setForm((f) => ({ ...f, brand_slug: e.target.value }))} /></Field>
      </div>
      <Field label="Category">
        <select className="ft-input" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}>
          {CATEGORIES.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
        </select>
      </Field>
      <Field label="Title *"><input className="ft-input" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} data-testid="admin-vault-link-title" /></Field>
      <Field label="URL *"><input className="ft-input" value={form.external_url} onChange={(e) => setForm((f) => ({ ...f, external_url: e.target.value }))} placeholder="https://…" data-testid="admin-vault-link-url" /></Field>
      <Field label="Description"><textarea className="ft-input min-h-[70px]" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} /></Field>
      <Footer onCancel={onClose} onSubmit={submit} loading={posting} submitLabel="Add Link" testid="admin-vault-link-submit" />
    </Modal>
  );
};

const Modal = ({ title, children, onClose, testid }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid={testid}>
    <button className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} aria-label="Close" />
    <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
      <div className="px-6 py-4 border-b border-[var(--ft-border)] flex items-center justify-between">
        <h3 className="text-lg font-bold tracking-tight">{title}</h3>
        <button onClick={onClose} className="ft-btn-ghost"><X size={16} /></button>
      </div>
      <div className="p-6 space-y-4">{children}</div>
    </div>
  </div>
);

const Footer = ({ onCancel, onSubmit, loading, submitLabel, testid }) => (
  <div className="pt-2 flex justify-end gap-2">
    <button onClick={onCancel} className="ft-btn-ghost">Cancel</button>
    <button onClick={onSubmit} disabled={loading} className="ft-btn-primary" data-testid={testid}>
      {loading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {submitLabel}
    </button>
  </div>
);

const Field = ({ label, children }) => (
  <div><label className="ft-label block mb-1.5">{label}</label>{children}</div>
);

export default AdminBrandVault;
